import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Printer, Monitor, Wifi, Usb, Info, Check, TestTube } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';

const PrintSettingsPage = () => {
  const { t } = useLanguage();
  const [paperSize, setPaperSize] = useState('80mm');
  const [autoPrint, setAutoPrint] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [showFooter, setShowFooter] = useState(true);
  const [testPrinting, setTestPrinting] = useState(false);

  const handleTestPrint = () => {
    setTestPrinting(true);
    const w = window.open('', '_blank', 'width=320,height=500');
    if (w) {
      const width = paperSize === '80mm' ? '80mm' : '58mm';
      w.document.write(`
        <html><head><title>Test Print</title>
        <style>
          @page { size: ${width} auto; margin: 0; }
          body { font-family: 'Courier New', monospace; width: ${width}; margin: 0; padding: 4mm; font-size: 12px; }
          .center { text-align: center; }
          .line { border-top: 1px dashed #000; margin: 6px 0; }
          .bold { font-weight: bold; }
        </style></head><body>
        <div class="center bold" style="font-size:16px">VendFactura</div>
        <div class="center" style="font-size:10px">Test Receipt / Ticket de Prueba</div>
        <div class="line"></div>
        <div>Paper: ${paperSize}</div>
        <div>Date: ${new Date().toLocaleString()}</div>
        <div class="line"></div>
        <div class="center" style="font-size:10px">If you can read this, your printer is configured correctly.</div>
        <div class="center" style="font-size:10px">Si puede leer esto, su impresora esta configurada.</div>
        <div class="line"></div>
        </body></html>
      `);
      w.document.close();
      w.focus();
      setTimeout(() => { w.print(); w.close(); setTestPrinting(false); }, 300);
    } else {
      setTestPrinting(false);
    }
  };

  return (
    <ShopLayout>
      <div className="space-y-6 max-w-2xl" data-testid="print-settings-page">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('print_title')}</h2>
          <p className="text-slate-500 text-sm">{t('print_subtitle')}</p>
        </div>

        {/* Connection Guide */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 p-4 rounded-sm"
        >
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-800 text-sm">{t('print_how_title')}</p>
              <ul className="text-blue-700 text-xs mt-2 space-y-1.5">
                <li className="flex items-center gap-2">
                  <Usb className="w-3.5 h-3.5" /> {t('print_usb_guide')}
                </li>
                <li className="flex items-center gap-2">
                  <Wifi className="w-3.5 h-3.5" /> {t('print_wifi_guide')}
                </li>
                <li className="flex items-center gap-2">
                  <Monitor className="w-3.5 h-3.5" /> {t('print_browser_guide')}
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Paper Size */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white border-2 border-slate-100 p-5"
        >
          <h3 className="font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
            <Printer className="w-4 h-4" />
            {t('print_paper_size')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {[{ value: '80mm', label: '80mm (Standard)' }, { value: '58mm', label: '58mm (Compact)' }].map(opt => (
              <button
                key={opt.value}
                onClick={() => setPaperSize(opt.value)}
                className={`p-4 border-2 rounded-sm text-left transition-colors ${
                  paperSize === opt.value
                    ? 'border-[#22c55e] bg-green-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`paper-${opt.value}`}
              >
                <div className="font-semibold text-[#1e3a5f]">{opt.label}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {opt.value === '80mm' ? t('print_80mm_desc') : t('print_58mm_desc')}
                </div>
                {paperSize === opt.value && (
                  <Check className="w-4 h-4 text-[#22c55e] mt-2" />
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Receipt Options */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white border-2 border-slate-100 p-5 space-y-4"
        >
          <h3 className="font-semibold text-[#1e3a5f]">{t('print_receipt_options')}</h3>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-slate-700">{t('print_auto_print')}</span>
            <button
              onClick={() => setAutoPrint(!autoPrint)}
              className={`w-12 h-6 rounded-full transition-colors ${autoPrint ? 'bg-[#22c55e]' : 'bg-slate-300'}`}
              data-testid="auto-print-toggle"
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${autoPrint ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-slate-700">{t('print_show_header')}</span>
            <button
              onClick={() => setShowHeader(!showHeader)}
              className={`w-12 h-6 rounded-full transition-colors ${showHeader ? 'bg-[#22c55e]' : 'bg-slate-300'}`}
              data-testid="show-header-toggle"
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${showHeader ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-slate-700">{t('print_show_footer')}</span>
            <button
              onClick={() => setShowFooter(!showFooter)}
              className={`w-12 h-6 rounded-full transition-colors ${showFooter ? 'bg-[#22c55e]' : 'bg-slate-300'}`}
              data-testid="show-footer-toggle"
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${showFooter ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </label>
        </motion.div>

        {/* Test Print */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={handleTestPrint}
            disabled={testPrinting}
            className="vf-btn-primary flex items-center gap-2 w-full justify-center"
            data-testid="test-print-btn"
          >
            {testPrinting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <TestTube className="w-5 h-5" />
            )}
            {t('print_test')}
          </button>
        </motion.div>
      </div>
    </ShopLayout>
  );
};

export default PrintSettingsPage;
